import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings as SettingsIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { acceptAll, rejectAll, saveConsent, getConsent } from "@/lib/cookieConsent";

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
  };

  const handleSavePrefs = () => {
    saveConsent({ analytics, marketing });
    setSettingsOpen(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        role="dialog"
        aria-label="Cookie consent"
        className="fixed bottom-0 inset-x-0 z-[100] p-2 sm:p-4 pointer-events-none pb-[max(0.5rem,env(safe-area-inset-bottom))]"
      >
        <div className="max-w-4xl mx-auto pointer-events-auto bg-card border border-border rounded-lg shadow-2xl p-3 sm:p-5">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <Cookie className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-[13px] sm:text-sm text-foreground mb-1">
                We use cookies
              </h2>
              <p className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed line-clamp-2 sm:line-clamp-none">
                Essential cookies + optional analytics. See our{" "}
                <Link to="/cookie-policy" className="text-primary hover:underline">
                  cookie policy
                </Link>
                .
              </p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2.5">
                <Button size="sm" onClick={handleAcceptAll} className="text-[11px] sm:text-xs h-7 sm:h-8 px-2.5 sm:px-3">
                  Accept all
                </Button>
                <Button size="sm" variant="outline" onClick={handleRejectAll} className="text-[11px] sm:text-xs h-7 sm:h-8 px-2.5 sm:px-3">
                  Reject all
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSettingsOpen(true)}
                  className="text-[11px] sm:text-xs h-7 sm:h-8 px-2.5 sm:px-3"
                >
                  <SettingsIcon className="w-3 h-3 mr-1" /> Settings
                </Button>
              </div>
            </div>
            <button
              onClick={handleRejectAll}
              aria-label="Close and reject optional cookies"
              className="text-muted-foreground hover:text-foreground p-1 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which categories of cookies you allow. You can change this anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div>
                <p className="text-sm font-medium">Necessary</p>
                <p className="text-xs text-muted-foreground">
                  Required for login, security and core features. Always on.
                </p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div>
                <p className="text-sm font-medium">Analytics</p>
                <p className="text-xs text-muted-foreground">
                  Help us understand how the app is used.
                </p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Marketing</p>
                <p className="text-xs text-muted-foreground">
                  Personalised content and offers.
                </p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleRejectAll}>Reject all</Button>
            <Button onClick={handleSavePrefs}>Save preferences</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}